/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import APIManager from "../../../Services/ApiManager/ApiManager";
import { IconButton, Typography } from "@mui/material";
import "./TaxTablePopUp.css";
import Grid from "@mui/material/Grid2";
import styles from "../../../page/ApplicationList/ApplicationList.module.css";
import { ClearIcon } from "@mui/x-date-pickers";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";

function createDataPrice(
  id,
  country,
  countryCode,
  currencyCode,
  price,
  taxRate,
  taxRateinPercent
) {
  return {
    id,
    country,
    countryCode,
    currencyCode,
    price,
    taxRate,
    taxRateinPercent,
  };
}

function TaxTablePopUp({ onClose, productData, type }) {
  const [productDetails, setProductDetails] = React.useState([]);
  const [prices, setPrices] = useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const frequencyUnitConverter = {
    "1-DAY": "Daily",
    "1-MONTH": "Monthly",
    "3-MONTH": "Quarterly",
    "6-MONTH": "Half yearly",
    "1-YEAR": "Yearly",
  };

  useEffect(() => {
    if (type !== 'InApp') {
      APIManager.getOEMAdminPlanDetails(
        productData.productId,
        productData.planId
      ).then((json) => {
        setProductDetails(json);
        let pricingArr = [];
        if (
          json.oneTimePlan?.planPrices?.length > 0
        ) {
          pricingArr = json.oneTimePlan.planPrices;
        } else if (
          json.recurringPlans?.[0]?.planPrices?.length > 0
        ) {
          pricingArr = json.recurringPlans[0].planPrices;
        }
        let allRows = [];
        pricingArr.forEach((price) => {
          allRows.push(
            createDataPrice(
              price.id,
              price.country,
              price.countryCode,
              price.currencyCode,
              price.price.toFixed(2),
              price.taxRate.toFixed(2),
              price.taxRateinPercent
            )
          );
        });
        setPrices(allRows);
      });
    }
    if (type === "InApp" && productData?.id) {
      APIManager.getProductDetails(
        productData.id,
        productData.type === "recurring"
      ).then((json) => {
        setProductDetails(json);
        if (
          json.oneTimePlan?.planPrices?.length > 0
        ) {
          let allRows = [];
          json.oneTimePlan.planPrices.forEach((price) => {
            allRows.push(
              createDataPrice(
                price.id,
                price.country,
                price.countryCode,
                price.currencyCode,
                price.price.toFixed(2),
                price.taxRate.toFixed(2),
                price.taxRateinPercent
              )
            );
          });
          setPrices(allRows);
        }
      });
    }
  }, [productData]);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.action.hover,
      fontWeight: "600",
      border: 0,
      paddingTop: 8,
      paddingBottom: 8,
      borderBottom: "1px solid #d0d0d0 !important",
      fontSize: 12,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "td, th": {
      border: 0,
      paddingTop: 5,
      paddingBottom: 5,
      borderBottom: "1px solid #e8e8e8",
    },
  }));

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = React.useMemo(
    () =>
      [...prices].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, prices]
  );

  return (
    <>
      <div className={styles.modalHeaderCls}>
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ fontWeight: "500", fontSize: "20px" }}
        >
          {(type === 'PremiumOne' || type === 'PremiumRec') ? ("App Pricing - Plan Details") : ("In-App Products Pricings")}          
        </Typography>
        <IconButton>
          {" "}
          <ClearIcon onClick={onClose} sx={{ padding: "none" }} />
        </IconButton>
      </div>
      <div style={{marginTop: "3%"}}>
        <div>
          {type === "InApp" && productDetails?.product && (
            <Grid container spacing={1}>
              <Grid size={4}>
                <Typography variant="subtitle2" gutterBottom>
                  <span className="product-details-modal-info-label">
                    Product Id:
                  </span>
                  <span className="product-details-modal-info-value">
                    {productDetails.product.productId}
                  </span>
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography variant="subtitle2" gutterBottom>
                  <span className="product-details-modal-info-label">
                    Plan Name:
                  </span>
                  <span className="product-details-modal-info-value">{productDetails.product.name}</span>
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography variant="subtitle2" gutterBottom>
                  <span className="product-details-modal-info-label">
                    Base Price:
                  </span>
                  <span className="product-details-modal-info-value">
                    {productDetails.oneTimePlan?.basePrice?.currencyCode}{" "}
                    {productDetails.oneTimePlan?.basePrice?.price.toFixed(2)}
                  </span>
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography variant="subtitle2" gutterBottom>
                  <span className="product-details-modal-info-label">
                    Trial Period:
                  </span>
                  <span className="product-details-modal-info-value">
                    {productDetails.trialPrice?.trialPeriod && (
                        <>
                          {productDetails.trialPrice.trialPeriod} Day
                          {productDetails.trialPrice.trialPeriod > 1 && <>s</>}
                        </>
                      )}
                    {!productDetails.trialPrice && <>0 Day</>}
                  </span>
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography variant="subtitle2" gutterBottom>
                  <span className="product-details-modal-info-label">
                    Recurrence:
                  </span>
                  <span className="product-details-modal-info-value">Disabled</span>
                </Typography>
              </Grid>
            </Grid>
          )}
          {type === "PremiumOne" &&
            productDetails?.product && (
              <Grid container spacing={1}>
                <Grid size={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    <span className="product-details-modal-info-label">
                      Plan Name:
                    </span>
                    <span className="product-details-modal-info-value">
                      {productData.name}
                    </span>
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    <span className="product-details-modal-info-label">
                      Base Price:
                    </span>
                    <span className="product-details-modal-info-value">
                      {productDetails.oneTimePlan?.basePrice?.currencyCode}{" "}
                      {productDetails.oneTimePlan?.basePrice?.price.toFixed(2)}
                    </span>
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    <span className="product-details-modal-info-label">
                      Preference:
                    </span>
                    <span className="product-details-modal-info-value">One Time Purchase</span>
                  </Typography>
                </Grid>
              </Grid>
            )}
          {(type === "PremiumRec" || type === "InAppPlan") &&
            productDetails?.product && (
              <Grid container spacing={1}>
                <Grid size={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    <span className="product-details-modal-info-label">
                      Plan Name:
                    </span>
                    <span className="product-details-modal-info-value">
                      {productDetails.recurringPlans[0]?.name}
                    </span>
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    <span className="product-details-modal-info-label">
                      Base Price:
                    </span>
                    <span className="product-details-modal-info-value">
                      {
                        productDetails.recurringPlans[0]?.basePrice
                          ?.currencyCode
                      }{" "}
                      {productDetails.recurringPlans[0]?.basePrice?.price.toFixed(2)}
                    </span>
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    <span className="product-details-modal-info-label">
                      Recurring Frequency:
                    </span>
                    <span className="product-details-modal-info-value">
                      {
                        frequencyUnitConverter[
                          productDetails.recurringPlans[0]?.recurringFrequency +
                            "-" +
                            productDetails.recurringPlans[0]
                              ?.recurringFrequencyUnit
                        ]
                      }
                    </span>
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    <span className="product-details-modal-info-label">
                      When To Charge:
                    </span>
                    <span className="product-details-modal-info-value">{productDetails.recurringPlans[0]?.chargeType === 'IMMEDIATE'
                        ? "Immediately"
                        : "End Of Billing Cycle"}</span>
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    <span className="product-details-modal-info-label">
                      Grace Period:
                    </span>
                    <span className="product-details-modal-info-value">
                      {productDetails.recurringPlans[0]?.gracePeriod} Day
                      {productDetails.recurringPlans[0]?.gracePeriod > 1 && "s"}
                    </span>
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    <span className="product-details-modal-info-label">
                      Auto Renew:
                    </span>
                    <span className="product-details-modal-info-value">
                      {productDetails.recurringPlans[0]?.autoRenew
                        ? "Enabled"
                        : "Disabled"}
                    </span>
                  </Typography>
                </Grid>
              </Grid>
            )}
            
          <div>
            <Typography variant="subtitle2" gutterBottom fontWeight={"bold"} sx={{marginTop: "2%"}}>
              Country specific price settings
            </Typography>
            {visibleRows.length > 0 && (
              <Box sx={{ width: "100%", marginTop: "3%" }}>
                <Paper sx={{ width: "100%", mb: 2 }} elevation={0}>
                  <TableContainer>
                    <Table aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Country</StyledTableCell>
                          <StyledTableCell>Price</StyledTableCell>
                          <StyledTableCell>Tax Rate</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleRows.map((price) => (
                          <StyledTableRow key={price.planId}>
                            <StyledTableCell
                              sx={{ fontWeight: "bold", width: "40%" }}
                            >
                              {price.country}
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ width: "30%", color: "#212121" }}
                            >
                              <span
                                style={{
                                  color: "#717273",
                                  marginRight: "10%",
                                }}
                              >
                                {price.currencyCode}
                              </span>
                              {price.price}
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ width: "30%", color: "#212121" }}
                            >
                              {price.taxRateinPercent +
                                "% (" +
                                price.currencyCode +
                                " " +
                                price.taxRate +
                                ")"}
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={prices.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Items per page"
                  />
                </Paper>
              </Box>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

TaxTablePopUp.propTypes = {
  onClose: PropTypes.func.isRequired,
  productData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    planId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    name: PropTypes.string,
  }),
  type: PropTypes.string.isRequired,
};

export default TaxTablePopUp;
